import { css } from "@linaria/core";
import cx from "classnames";
import React, { memo, useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router";
import { useUser } from "../../../../../../../api/useUser";
import { useHasUserRole } from "../../../../../../canUserDo";
import t from "../../../../../../../i18n";
import Badge from "@ui/Badge";
import Button from "@ui/Button";
import useModal, { useModals } from "@ui/lib/hooks/useModal";
import { colors, spacings } from "@ui/Theme";
import useToasts from "@ui/Toast/useToasts";
import TooltipV2 from "@ui/TooltipV2";
import ValidityDot from "@ui/ValidityDot";
import { DashboardMenu } from "../../../../component/DashboardMenu";
import { ModalDashboardQuitEditing } from "../../../../component/TransactionalModals/ModalDashboardQuitEditing";
import { useDashboardDuplicateCallback } from "../../../../hook/action/duplicate";
import {
  useDashboardLocks,
  useLockDashboardCallback,
  useUnlockDashboardCallback,
} from "../../../../hook/action/lock";
import { useDashboardSaveCallback } from "../../../../hook/action/save";
import { useDashboardEditorContext } from "../../../../hook/context/editor";
import { useDashboardEditorState } from "../../../../hook/store/editor";
import { useDashboardPermissions } from "../../../../hook/useDashboardPermissions";
import { useDashboardUnsaved } from "../../../../hook/useDashboardUnsaved";
import { printableHiddenInline } from "../../../../utils/print";
import ModalEditLinkedDashboard from "../../../../component/TransactionalModals/ModalDashboardLinkedEvent";
import { useDashboardLinkedFaultEvents } from "../../../../../../../api/useFaultEventsDashboards";
import { useTranslations } from "../../../../../../../api/useTranslations";
import { toPath, useMatchPattern } from "../../../../../../../router/helpers/path";
import { useSummaryDashboards } from "../../../../../../../api/useSummaryDashboards";
import { indexBy } from "diagnostics-lib/iterable";
import { useDashboard } from "../../../../hook/api/dashboard";

export const Actions = memo(
  ({
    projectId,
    dashboardId,
    className,
  }: {
    projectId: string;
    dashboardId: string;
    className?: string;
  }) => {
    const translations = useTranslations();
    const navigate = useNavigate();
    const { addToast } = useToasts();
    const linkedFaultEvents = useDashboardLinkedFaultEvents(projectId, dashboardId);
    const summaryDashboards = useSummaryDashboards(projectId);
    const indexedSummaryDashboards = useMemo(
      () => indexBy(summaryDashboards, d => d.dashboardId),
      [summaryDashboards],
    );

    const editable = Boolean(useMatchPattern("/projects/:projectId/dashboards/:dashboardId/edit"));
    const { dashboard, blocks } = useDashboard(projectId, dashboardId);
    const editor = useDashboardEditorContext();
    const editorState = useDashboardEditorState(editor?.store);
    const user = useUser();
    const isSuperAdmin = useHasUserRole(user, "SUPER_ADMIN");
    const [unsaved, setUnsaved] = useDashboardUnsaved({ scope: dashboardId });
    const { lockedBy, isUserLocked, isLocked } = useDashboardLocks(projectId);
    const isVisibleEditModal = React.useMemo(
      () =>
        (linkedFaultEvents.length > 0 || indexedSummaryDashboards.has(dashboardId)) &&
        dashboard?.published,
      [linkedFaultEvents, dashboard?.published, dashboardId, indexedSummaryDashboards],
    );

    const isAuthor = dashboard?.createdByUserId === user.id;
    const forbidSuperAdminEditUnpublished = isSuperAdmin && !dashboard?.published && !isAuthor;

    const {
      modal: editLinkedDashboardModal,
      openModal: openEditLinkedDashboardModal,
      closeModal: closeEditLinkedDashboardModal,
    } = useModal();
    const duplicateDashboardCallback = useDashboardDuplicateCallback();
    const onDuplicate = useCallback(async () => {
      if (!dashboard) return false;
      try {
        const result = await duplicateDashboardCallback({
          ...dashboard,
          title: `${dashboard.title}_copy`,
          published: true,
        });
        if (!result) throw new Error();
        addToast("success", t("project.dashboard.actions.duplicate.success", dashboard.title));
        void navigate(
          toPath("/projects/:projectId/dashboards/:dashboardId", {
            projectId: dashboard.projectId,
            dashboardId: result.id,
          }),
        );
        return true;
      } catch (e) {
        addToast("error", t("project.dashboard.actions.duplicate.error"));
        return false;
      } finally {
        closeEditLinkedDashboardModal();
      }
    }, [addToast, dashboard, duplicateDashboardCallback, navigate, closeEditLinkedDashboardModal]);

    const lockDashboard = useLockDashboardCallback(projectId);
    const enterEditMode = useCallback(async () => {
      if (!editor || !dashboard || !blocks) return;
      await lockDashboard(dashboard.id);
      await editor?.clear();
      await editor?.setup({ dashboard, blocks });
      void navigate(
        toPath("/projects/:projectId/dashboards/:dashboardId/edit", {
          projectId: dashboard.projectId,
          dashboardId: dashboard.id,
        }),
      );
    }, [blocks, dashboard, editor, navigate, lockDashboard]);
    const { modal, openModal, closeModal } = useModals<"quit">();

    const unlockDashboard = useUnlockDashboardCallback(projectId);
    const cancelChanges = useCallback(async () => {
      if (unsaved) {
        return openModal("quit");
      }

      const unlocked = await unlockDashboard(dashboardId);
      if (!unlocked) return;

      editor?.focus(null);
      void editor?.clear?.();
      void navigate(
        toPath("/projects/:projectId/dashboards/:dashboardId", {
          projectId,
          dashboardId,
        }),
      );
    }, [dashboardId, editor, navigate, openModal, projectId, unlockDashboard, unsaved]);

    const saveDashboard = useDashboardSaveCallback();
    const saveChanges = React.useCallback(() => {
      if (!editorState || !editorState?.dashboard) {
        addToast("error", t("project.dashboard.actions.save.error"));
        return true;
      }
      return saveDashboard({
        toUpdateDashboard: editorState.dashboard,
        toUpdateBlocks: editorState.blocks,
      })
        .then(() => {
          setUnsaved(false);
          addToast(
            "success",
            t(
              "project.dashboard.actions.save.success",
              translations,
              editorState.dashboard.published,
            ),
          );
          return true;
        })
        .catch(() => {
          addToast("error", t("project.dashboard.actions.save.error"));
          return true;
        });
    }, [addToast, editorState, saveDashboard, setUnsaved, translations]);

    const dashboardPermissions = useDashboardPermissions(dashboard ?? null);

    const editHint = useMemo(
      () =>
        isSuperAdmin && isLocked(dashboardId) && !isUserLocked(dashboardId)
          ? t("project.dashboard.actions.admin_edit.hint", translations)
          : !dashboardPermissions.allowSharing && dashboard?.readonly
            ? t("project.dashboard.actions.edit.lock.hint", translations)
            : "",
      [
        dashboard?.readonly,
        dashboardId,
        dashboardPermissions.allowSharing,
        isLocked,
        isUserLocked,
        isSuperAdmin,
        translations,
      ],
    );

    useEffect(() => {
      function hotkeyHandler(e: KeyboardEvent) {
        if ((e.ctrlKey || e.metaKey) && e.key === "s" && editable && !editor?.error) {
          e.preventDefault();
          void saveChanges();
        }
      }
      window.addEventListener("keydown", hotkeyHandler);
      return () => window.removeEventListener("keydown", hotkeyHandler);
    }, [saveChanges, editable, editor?.error]);

    return (
      <div className={cx(actionContainer, className)}>
        {!isUserLocked(dashboardId) && isLocked(dashboardId) && (
          <Badge
            className={actionEditBadge}
            backgroundColor={colors.lightNitrogen100Fade050}
            color={colors.carbon900}
          >
            <ValidityDot state="info" />
            {t("project.dashboard.state.editing", lockedBy(dashboardId))}
          </Badge>
        )}
        {editable ? (
          <>
            {unsaved && (
              <Badge
                backgroundColor={colors.oxygen50}
                color={colors.carbon900}
                icon={<ValidityDot state="error" />}
                className={unsavedBadge}
              >
                {t("project.dashboard.state.unsaved")}
              </Badge>
            )}
            {isUserLocked(dashboardId) && (
              <>
                <Button variant="ghost" onClick={cancelChanges}>
                  {t("project.dashboard.actions.cancel")}
                </Button>
                <Button
                  variant="primary"
                  onClick={saveChanges}
                  disabled={editor?.error ? Boolean(editor?.error) : !unsaved}
                >
                  {t("project.dashboard.actions.save")}
                </Button>
              </>
            )}
          </>
        ) : (
          <>
            {dashboardPermissions.allowEditing && (
              <TooltipV2 overlay={editHint}>
                <div>
                  <Button
                    variant="primary"
                    onClick={isVisibleEditModal ? openEditLinkedDashboardModal : enterEditMode}
                    disabled={
                      forbidSuperAdminEditUnpublished ||
                      (isLocked(dashboardId) && !isUserLocked(dashboardId))
                    }
                  >
                    {t("project.dashboard.actions.edit")}
                  </Button>
                </div>
              </TooltipV2>
            )}
          </>
        )}
        {dashboard && !editable && <DashboardMenu dashboard={dashboard} withPrintOption />}
        {dashboard && (
          <ModalEditLinkedDashboard
            editModal={editLinkedDashboardModal}
            closeEditModal={closeEditLinkedDashboardModal}
            dashboard={dashboard}
            linkedEventCount={linkedFaultEvents.length}
            isLinkedToHome={indexedSummaryDashboards.has(dashboardId)}
            onEdit={enterEditMode}
            onDuplication={onDuplicate}
          />
        )}
        <ModalDashboardQuitEditing
          projectId={projectId}
          dashboardId={dashboardId}
          opened={modal === "quit"}
          close={closeModal}
        />
      </div>
    );
  },
);

const actionContainer = css`
  display: flex;
  align-items: center;
  gap: ${spacings.xs};

  ${printableHiddenInline}

  > * {
    flex-shrink: 0;
  }
`;

const unsavedBadge = css`
  transition:
    opacity 100ms ease-in-out,
    visibility 100ms ease-in-out;
  margin-right: ${spacings.xs};
`;

const actionEditBadge = css`
  svg:first-child {
    color: ${colors.darkNitrogen600};
    margin-right: ${spacings.xs};
  }
`;
